import React from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";

export default function StoreProductsTable({
  products,
  onEdit,
  onDelete,
  canManage,
  actionLoadingId,
}) {
  if (!products.length) {
    return (
      <Paper
        sx={{
          borderRadius: "18px",
          border: "1px solid rgba(148,163,184,0.18)",
          boxShadow: "0 18px 40px rgba(15,23,42,0.06)",
          py: 5,
          px: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: 13.4,
            fontWeight: 500,
            color: "#64748b",
          }}
        >
          No hay productos disponibles.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        borderRadius: "18px",
        border: "1px solid rgba(148,163,184,0.18)",
        boxShadow: "0 18px 40px rgba(15,23,42,0.06)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1.2fr 0.8fr",
            md: "1.4fr 0.9fr 0.9fr 0.7fr 1fr",
          },
          gap: 1,
          px: { xs: 1.6, md: 2 },
          py: 1.3,
          bgcolor: "#f8fafc",
          borderBottom: "1px solid rgba(148,163,184,0.16)",
        }}
      >
        <Typography sx={{ fontSize: 12.6, fontWeight: 600, color: "#475569" }}>
          Producto
        </Typography>
        <Typography
          sx={{
            display: { xs: "none", md: "block" },
            fontSize: 12.6,
            fontWeight: 600,
            color: "#475569",
          }}
        >
          Catálogo
        </Typography>
        <Typography
          sx={{
            fontSize: 12.6,
            fontWeight: 600,
            color: "#475569",
          }}
        >
          Precio
        </Typography>
        <Typography
          sx={{
            display: { xs: "none", md: "block" },
            fontSize: 12.6,
            fontWeight: 600,
            color: "#475569",
          }}
        >
          Estado
        </Typography>
        <Typography
          sx={{
            display: { xs: "none", md: "block" },
            fontSize: 12.6,
            fontWeight: 600,
            color: "#475569",
          }}
        >
          Acciones
        </Typography>
      </Box>

      {products.map((item) => (
        <Box
          key={item.uuid}
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1.2fr 0.8fr",
              md: "1.4fr 0.9fr 0.9fr 0.7fr 1fr",
            },
            gap: 1,
            alignItems: "center",
            px: { xs: 1.6, md: 2 },
            py: 1.25,
            borderBottom: "1px solid rgba(148,163,184,0.12)",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: "#0f172a",
                fontSize: 13.4,
                fontWeight: 600,
                lineHeight: 1.35,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {item.name}
            </Typography>

            <Typography
              sx={{
                color: "#64748b",
                fontSize: 12.2,
                fontWeight: 500,
                lineHeight: 1.35,
                mt: 0.3,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {item.product_code}
            </Typography>
          </Box>

          <Box sx={{ display: { xs: "none", md: "block" }, minWidth: 0 }}>
            <Typography
              sx={{
                color: "#334155",
                fontSize: 13,
                fontWeight: 500,
                lineHeight: 1.35,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {item.section_name} / {item.category_name}
            </Typography>

            <Typography
              sx={{
                color: "#64748b",
                fontSize: 12.2,
                fontWeight: 500,
                lineHeight: 1.35,
                mt: 0.3,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {item.brand_name}
            </Typography>
          </Box>

          <Box>
            <Typography
              sx={{
                color: "#0f172a",
                fontSize: 13.2,
                fontWeight: 600,
              }}
            >
              ${item.base_price}
            </Typography>

            <Typography
              sx={{
                color: "#64748b",
                fontSize: 12.2,
                fontWeight: 500,
                mt: 0.2,
              }}
            >
              Oferta: {item.offer_price ? `$${item.offer_price}` : "—"}
            </Typography>
          </Box>

          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <Chip
              label={item.is_active ? "Activo" : "Inactivo"}
              size="small"
              sx={{
                height: 26,
                borderRadius: "8px",
                fontSize: 12,
                fontWeight: 600,
                bgcolor: item.is_active
                  ? "rgba(34,197,94,0.12)"
                  : "rgba(220,38,38,0.12)",
                color: item.is_active ? "#166534" : "#dc2626",
              }}
            />
          </Box>

          <Stack
            direction="row"
            spacing={0.8}
            justifyContent="flex-start"
            sx={{ display: { xs: "none", md: "flex" } }}
          >
            {canManage && (
              <>
                <Button
                  onClick={() => onEdit(item)}
                  variant="outlined"
                  sx={{
                    height: 34,
                    px: 1.2,
                    borderRadius: "9px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 12.6,
                    color: "#334155",
                    borderColor: "rgba(51,65,85,0.18)",
                    "&:hover": {
                      borderColor: "rgba(51,65,85,0.28)",
                      bgcolor: "rgba(148,163,184,0.08)",
                    },
                  }}
                >
                  Editar
                </Button>

                <Button
                  onClick={() => onDelete(item)}
                  variant="outlined"
                  disabled={actionLoadingId === `delete-${item.uuid}`}
                  startIcon={
                    actionLoadingId === `delete-${item.uuid}` ? (
                      <CircularProgress size={14} />
                    ) : (
                      <DeleteOutlineRoundedIcon />
                    )
                  }
                  sx={{
                    height: 34,
                    px: 1.2,
                    borderRadius: "9px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 12.6,
                    color: "#dc2626",
                    borderColor: "rgba(220,38,38,0.22)",
                    "&:hover": {
                      borderColor: "rgba(220,38,38,0.32)",
                      bgcolor: "rgba(220,38,38,0.06)",
                    },
                  }}
                >
                  Eliminar
                </Button>
              </>
            )}
          </Stack>
        </Box>
      ))}
    </Paper>
  );
}